// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { sendSuccessMessage } from "@modules/app/actionCreator";

import actionCreators from "@modules/shared/actionCreator";
import {
  GetAlerts,
  GetAlertsSuppressedBy,
  GetAlertsSuppression,
  DeleteAlertsSuppression,
  AddAlertsSuppression,
  GetAlertsSuppressionOptions,
} from "@modules/shared/constants/actions";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ListRequest = $Exact<{
  clientId: number,
  alertId?: number,
  suppressionAlertId?: number,
}>;

export function* onGetAlerts({
  payload,
}: Action<ListRequest>): Generator<*, void, { data: ListResponse<Array<Object>> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration`,
    );

    yield put(actionCreators.GetAlertsSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetAlertsError(e.response));
  }
}

export function* onGetAlertsSuppressedBy({
  payload,
}: Action<ListRequest>): Generator<*, void, { data: ListResponse<Array<Object>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/client/${payload.clientId}/alert/configuration/${payload.alertId}/suppressed-by`,
    );
    yield put(actionCreators.GetAlertsSuppressedBySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAlertsSuppressedByError(e.response));
  }
}

export function* onGetAlertsSuppression({
  payload,
}: Action<ListRequest>): Generator<*, void, { data: ListResponse<Array<Object>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/client/${payload.clientId}/alert/configuration/${payload.alertId}/suppression`,
    );
    yield put(actionCreators.GetAlertsSuppressionSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAlertsSuppressionError(e.response));
  }
}

export function* onDeleteAlertsSuppression({
  payload,
}: Action<ListRequest>): Generator<*, void, { data: ListResponse<Array<Object>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.suppressionAlertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.delete,
      `api/v2/client/${payload.clientId}/alert/configuration/${payload.alertId}/suppression/${payload.suppressionAlertId}`,
    );

    yield all([
      put(actionCreators.GetAlertsSuppression(payload)),
      put(actionCreators.DeleteAlertsSuppressionSuccess()),
      put(sendSuccessMessage("SUCCESSFULLY_DELETED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAlertsSuppressionError(e.response));
  }
}

export function* onAddAlertsSuppression({
  payload,
}: Action<ListRequest>): Generator<*, void, { data: ListResponse<Array<Object>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.suppressionAlertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/client/${payload.clientId}/alert/configuration/${payload.alertId}/suppression`,
      { alert_id: payload.suppressionAlertId },
    );

    yield all([
      put(actionCreators.AddAlertsSuppressionSuccess(response.data)),
      put(actionCreators.GetAlertsSuppressionOptions(payload)),
      put(sendSuccessMessage("SUCCESSFULLY_ADDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAlertsSuppressionError(e.response));
  }
}

export function* onGetAlertsSuppressionOptions({
  payload,
}: Action<ListRequest>): Generator<*, void, { data: ListResponse<Array<Object>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/client/${payload.clientId}/alert/configuration/${payload.alertId}/suppression/options`,
    );
    yield put(actionCreators.GetAlertsSuppressionOptionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAlertsSuppressionOptionsError(e.response));
  }
}

export function* AlertsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAlerts, onGetAlerts),
    takeEvery(GetAlertsSuppressedBy, onGetAlertsSuppressedBy),
    takeEvery(GetAlertsSuppression, onGetAlertsSuppression),
    takeEvery(DeleteAlertsSuppression, onDeleteAlertsSuppression),
    takeEvery(AddAlertsSuppression, onAddAlertsSuppression),
    takeEvery(GetAlertsSuppressionOptions, onGetAlertsSuppressionOptions),
  ]);
}

export default AlertsSagas;
